import { createContext, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  type CreateAgentFormData,
  CreateAgentModal,
} from '@/components/modals/CreateAgentModal'
import { CreateChatModeModal } from '@/components/modals/CreateChatModeModal'
import { useAppcues } from '@/hooks/useAppcues'
import { useCollections } from '@/hooks/useCollections'
import { useConfigurationMutations } from '@/hooks/useConfigurationMutations'
import { useMixpanel } from '@/hooks/useMixpanel'
import { buildConfigurationFromGeneratedRules } from '@/lib/buildConfigurationFromGeneratedRules'
import { routePaths } from '@/lib/router'
import type { GenerateRulesResponse } from '@/services/chatBuilder'
import { CREATE_CHAT_EVENT } from '@/services/mixpanel'
import type {
  CreateAgentContextValue,
  CreateAgentProviderProps,
} from './CreateAgentContext.types'

const CreateAgentContext = createContext<CreateAgentContextValue | undefined>(
  undefined
)

export function CreateAgentProvider({ children }: CreateAgentProviderProps) {
  const navigate = useNavigate()
  const { track } = useMixpanel()
  const { track: trackAppcues } = useAppcues()
  const { collections } = useCollections()
  const { createConfiguration, generateRules, isCreating, isGeneratingRules } =
    useConfigurationMutations()
  const [open, setOpen] = useState(false)
  const [modeOpen, setModeOpen] = useState(false)
  const isCreatingAgent = isCreating || isGeneratingRules
  const value = useMemo(
    () => ({
      openCreateAgent: () => setModeOpen(true),
      isCreatingAgent,
    }),
    [isCreatingAgent]
  )
  const handleGuidedSetup = () => {
    setModeOpen(false)
    navigate(`${routePaths.chatBuilder}/guided`)
  }
  const handleFullBuilder = () => {
    setModeOpen(false)
    setOpen(true)
  }
  const handleConfirm = async (formData: CreateAgentFormData) => {
    try {
      let agentName = 'New Agent'
      const description = formData.instructions
      const collectionId = formData.collectionId || ''
      let collectionName = ''

      let generated: GenerateRulesResponse['data'] | undefined

      if (collectionId) {
        const selectedCollection = collections.find(
          (collection) => collection.id === collectionId
        )

        collectionName = selectedCollection?.name || ''
      }

      if (formData.instructions.trim()) {
        try {
          const result = await generateRules({
            instructions: formData.instructions,
            collectionId: formData.collectionId,
          })

          generated = result.data

          if (result.data?.agentName) {
            agentName = result.data.agentName
          }
        } catch (error) {
          console.error(
            'Rule generation failed, creating with defaults:',
            error
          )

          toast.error('Rule generation failed', {
            description:
              'Agent will be created with default rules. You can generate rules later from the Rules tab.',
          })
        }
      }

      const request = buildConfigurationFromGeneratedRules({
        generated,
        instructions: description,
        deploymentType: formData.deploymentType,
        collectionId,
        collectionName,
      })

      request.name = agentName

      const newConfig = await createConfiguration(request)

      track(CREATE_CHAT_EVENT)
      trackAppcues('Agent Created')

      setOpen(false)

      navigate(`${routePaths.chatBuilder}/${newConfig.id}`)
    } catch (error) {
      console.error('Failed to create new configuration:', error)

      toast.error('Creation Failed', {
        description: 'Failed to create new agent. Please try again.',
      })
    }
  }

  return (
    <CreateAgentContext.Provider value={value}>
      {children}
      <CreateChatModeModal
        open={modeOpen}
        onOpenChange={setModeOpen}
        onGuidedSetup={handleGuidedSetup}
        onFullBuilder={handleFullBuilder}
      />
      <CreateAgentModal
        open={open}
        onOpenChange={setOpen}
        onConfirm={handleConfirm}
        isLoading={isCreatingAgent}
        collections={collections}
      />
    </CreateAgentContext.Provider>
  )
}

export function useCreateAgent(): CreateAgentContextValue {
  const context = useContext(CreateAgentContext)

  if (context === undefined) {
    throw new Error('useCreateAgent must be used within a CreateAgentProvider')
  }

  return context
}
